<template>
  <v-container
    id="data-tables"
    tag="section"
    fluid
  >
    <base-material-card
      color="indigo"
      icon="mdi-account-multiple"
      inline
      :class="['elevation-4', $vuetify.breakpoint.xs ? 'px-3 py-3' : 'px-5 py-4']"
    >
      <template v-slot:after-heading>
        <div :class="$vuetify.breakpoint.xs ? 'text-h4' : 'display-2'" class="font-weight-light">
          Users Management
        </div>
      </template>

      <v-row class="mt-2 align-center">
        <v-col cols="12" sm="12" md="6" lg="4">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Search by email, ID, provider, or system domain..."
            outlined
            dense
            clearable
            hide-details
            class="search-field"
          />
        </v-col>
        <v-col cols="12" sm="12" md="6" lg="8" class="text-right">
          <v-btn
            color="indigo"
            dark
            :small="$vuetify.breakpoint.xs"
            :class="$vuetify.breakpoint.xs ? 'mt-2' : ''"
            @click="addUser"
          >
            <v-icon left :small="$vuetify.breakpoint.xs">mdi-account-plus</v-icon>
            Add User
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="mt-2 mb-4" />

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="indigo"
          size="64"
          width="6"
        />
        <div class="mt-4 text-h6 grey--text font-weight-light">
          Loading users...
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredItems.length === 0" class="text-center py-12">
        <v-icon size="96" color="grey lighten-2" class="mb-4">mdi-account-off-outline</v-icon>
        <div class="text-h6 grey--text font-weight-light mb-2">
          {{ search ? 'No users found' : 'No users available' }}
        </div>
        <div class="text-body-2 grey--text mb-4">
          {{ search ? 'Try adjusting your search criteria' : 'Users will appear here once added' }}
        </div>
        <v-btn
          v-if="search"
          color="indigo"
          outlined
          @click="search = ''"
        >
          <v-icon left>mdi-close</v-icon>
          Clear Search
        </v-btn>
      </div>

      <!-- Data Table -->
      <v-data-table
        v-else
        :headers="computedHeaders"
        :items="filteredItems"
        :search="search"
        class="modern-table"
        :item-class="() => 'table-row'"
        item-key="id"
        :mobile-breakpoint="0"
        :disable-pagination="$vuetify.breakpoint.xs"
        :hide-default-footer="$vuetify.breakpoint.xs"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100],
          'items-per-page-text': 'Rows per page:',
          'show-first-last-page': true
        }"
        show-expand
        :expanded="expanded"
      >
        <template v-slot:item.id="{ item }">
          <div class="d-flex flex-column compact-id">
            <span class="text-body-2 font-weight-bold grey--text text--darken-2">
              #{{ item.id }}
            </span>
            <span
              class="provider-badge mt-1"
              :style="getProviderBadgeStyle(item.provider)"
            >
              {{ item.provider || 'N/A' }}
            </span>
          </div>
        </template>

        <template v-slot:item.email="{ item }">
          <span class="font-weight-medium text-body-2">{{ item.email }}</span>
        </template>

        <template v-slot:item.system_domain="{ item }">
          <span class="text-body-2">{{ item.system_domain || '—' }}</span>
        </template>

        <template v-slot:item.api_host="{ item }">
          <span class="text-body-2">{{ item.provider === 'infocab' ? item.infocab_host : item.provider === 'icabbi' ? item.icabbi_host : item.provider === 'autocab' ? item.api_host : 'N/A' }}</span>
        </template>

        <template v-slot:item.api_username="{ item }">
          <span class="text-body-2">{{ item.api_username || 'N/A' }}</span>
        </template>

        <template v-slot:item.api_password="{ item }">
          <div class="d-flex align-center password-cell" @click.stop="togglePassword(item.id)">
            <span v-if="visiblePasswords[item.id]" class="font-weight-medium text-body-2 password-visible">
              {{ item.provider === 'infocab' ? item.infocab_token : item.provider === 'icabbi' ? item.icabbi_token : item.api_password || 'N/A' }}
            </span>
            <span v-else class="text-body-2 password-masked">
              ••••••••••••
            </span>
            <v-icon
              x-small
              :color="visiblePasswords[item.id] ? 'grey' : 'grey lighten-1'"
              class="ml-2 password-toggle-icon"
            >
              {{ visiblePasswords[item.id] ? 'mdi-eye-off' : 'mdi-eye' }}
            </v-icon>
          </div>
        </template>

        <template v-slot:item.companies="{ item }">
          <div v-if="item.companies && item.companies.length > 0" class="companies-display-modern">
            <div class="companies-count-badge">
              <v-icon x-small color="indigo" class="mr-1">mdi-domain</v-icon>
              <span class="companies-count-number">{{ item.companies.length }}</span>
            </div>
            <div class="companies-names">
              <span
                v-for="(company, index) in item.companies.slice(0, 2)"
                :key="company.id"
                class="company-name-modern"
              >
                {{ company.name }}<span v-if="index < Math.min(2, item.companies.length - 1)" class="company-separator-modern">,</span>
              </span>
              <span
                v-if="item.companies.length > 2"
                class="company-more-modern clickable"
                @click.stop="toggleExpand(item)"
              >
                +{{ item.companies.length - 2 }} more
              </span>
            </div>
          </div>
          <span v-else class="text-body-2 grey--text text--lighten-1">—</span>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="expanded-row">
            <div class="expanded-companies-content">
              <div class="expanded-companies-header">
                <v-icon small color="indigo" class="mr-2">mdi-domain</v-icon>
                <span class="text-body-1 font-weight-bold">All Companies ({{ item.companies ? item.companies.length : 0 }})</span>
              </div>
              <div class="expanded-companies-list">
                <v-chip
                  v-for="company in item.companies"
                  :key="company.id"
                  small
                  outlined
                  color="indigo"
                  class="ma-1"
                >
                  {{ company.name }}
                </v-chip>
              </div>
            </div>
          </td>
        </template>


        <template v-slot:item.actions="{ item }">
          <div class="actions-cell">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  color="indigo"
                  class="action-btn"
                  @click="editItem(item)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit User</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  color="error"
                  class="action-btn"
                  @click="setDelDialog(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete User</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </base-material-card>
    <v-dialog
      v-model="delDialog"
      :max-width="$vuetify.breakpoint.xs ? '95%' : '500'"
      persistent
      overlay-opacity="0.7"
    >
      <v-card class="elevation-12 delete-dialog">
        <v-card-title class="headline error white--text py-4">
          <v-avatar size="40" color="white" class="mr-3">
            <v-icon color="error" size="24">mdi-alert-circle</v-icon>
          </v-avatar>
          <span>Confirm Deletion</span>
        </v-card-title>

        <v-card-text class="pt-6 pb-2">
          <div class="text-h6 mb-4 font-weight-regular">
            Are you sure you want to delete this user?
          </div>
          <v-alert
            type="warning"
            outlined
            prominent
            border="left"
            class="mb-0"
          >
            <div class="font-weight-medium">Warning:</div>
            <div class="text-caption mt-1">This action cannot be undone. All user data will be permanently deleted.</div>
          </v-alert>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer />
          <v-btn
            text
            large
            class="mr-3"
            @click="delDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            large
            depressed
            class="px-6"
            @click="deleteitem()"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import { _list, _delete } from '@/services/User'
  export default {
    name: 'DashboardDataTables',
    data: () => ({
      delDialog: false,
      loading: false,
      headers: [
        { text: 'ID', value: 'id', width: '80px', align: 'left' },
        { text: 'Email', value: 'email', sortable: true },
        { text: 'System Domain', value: 'system_domain', sortable: true },
        { text: 'API Host', value: 'api_host', sortable: false },
        { text: 'API Username', value: 'api_username', sortable: false },
        { text: 'API Password/Token', value: 'api_password', sortable: false },
        { text: 'Companies', value: 'companies', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px', align: 'center' },
      ],
      items: [],
      search: undefined,
      deletedIndex: -1,
      visiblePasswords: {},
      expanded: [],
    }),
    computed: {
      filteredItems () {
        if (!this.search) return this.items

        const searchLower = this.search.toLowerCase()
        return this.items.filter(item => {
          return (
            (item.email && item.email.toLowerCase().includes(searchLower)) ||
            (item.id && item.id.toString().includes(searchLower)) ||
            (item.provider && item.provider.toLowerCase().includes(searchLower)) ||
            (item.system_domain && item.system_domain.toLowerCase().includes(searchLower))
          )
        })
      },
      computedHeaders () {
        const breakpoint = this.$vuetify.breakpoint
        return this.headers.map(header => {
          // Hide less important columns on mobile
          if (breakpoint.xs) {
            if (['companies', 'api_host', 'api_username', 'api_password', 'system_domain'].includes(header.value)) {
              return { ...header, class: 'd-none' }
            }
          } else if (breakpoint.sm) {
            // On small screens, hide API Username
            if (header.value === 'api_username') {
              return { ...header, class: 'd-none' }
            }
          }
          return header
        })
      },
    },
    mounted () {
      this.loadData()
    },
    methods: {
      loadData () {
        this.loading = true
        _list().then(res => {
          this.items = res.data.obj.list
          this.loading = false
        }, err => {
          console.log(err.data)
          this.loading = false
        })
      },
      editItem (item) {
        this.$router.push(`user/${item.id}/edit`)
      },
      addUser () {
        this.$router.push('user/add')
      },
      setDelDialog (item) {
        this.deletedIndex = item.id
        this.delDialog = true
      },
      deleteitem () {
        _delete(this.deletedIndex).then(() => {
          console.log('delete')
          this.delDialog = false
          let toDelete = this.deletedIndex
          this.items = this.items.filter(function (el) {
            return el.id !== toDelete
          })
        }, error => {
          this.delDialog = false
          console.log(error.data)
        }).catch(error => {
          console.log(error.message)
        })
      },
      maskPassword (password) {
        if (!password) return 'N/A'
        if (password.length <= 4) return '****'
        return '•'.repeat(Math.min(password.length, 12))
      },
      getProviderColor (provider) {
        const colors = {
          infocab: 'primary',
          autocab: 'orange',
          other: 'grey',
        }
        return colors[provider] || 'grey'
      },
      getProviderBadgeStyle (provider) {
        const providerLower = (provider || 'n/a').toLowerCase()
        const colorMap = {
          infocab: {
            color: '#1976d2',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            borderColor: 'rgba(25, 118, 210, 0.3)'
          },
          autocab: {
            color: '#f44336',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            borderColor: 'rgba(244, 67, 54, 0.3)'
          },
          icabbi: {
            color: '#7b1fa2',
            backgroundColor: 'rgba(123, 31, 162, 0.1)',
            borderColor: 'rgba(123, 31, 162, 0.3)'
          },
          default: {
            color: '#757575',
            backgroundColor: 'rgba(117, 117, 117, 0.1)',
            borderColor: 'rgba(117, 117, 117, 0.3)'
          }
        }
        const style = colorMap[providerLower] || colorMap.default
        return {
          color: style.color,
          backgroundColor: style.backgroundColor,
          borderColor: style.borderColor
        }
      },
      getProviderIcon (provider) {
        const icons = {
          infocab: 'mdi-taxi',
          other: 'mdi-server',
        }
        return icons[provider] || 'mdi-help-circle'
      },
      togglePassword (itemId) {
        this.$set(this.visiblePasswords, itemId, !this.visiblePasswords[itemId])
      },
      toggleExpand (item) {
        const index = this.expanded.indexOf(item)
        if (index > -1) {
          this.expanded.splice(index, 1)
        } else {
          this.expanded.push(item)
        }
      },
    },
  }
</script>

<style scoped>
  .modern-table >>> thead th {
    font-weight: 600 !important;
    font-size: 11px !important;
    letter-spacing: 0.3px;
    color: #424242 !important;
    text-transform: uppercase;
    background-color: #f5f5f5;
    border-bottom: 2px solid #e0e0e0 !important;
    padding: 8px 12px !important;
    height: 40px !important;
    white-space: nowrap !important;
  }

  .modern-table >>> tbody td {
    font-size: 13px;
    padding: 6px 12px !important;
    height: auto !important;
    vertical-align: middle;
  }

  .modern-table >>> tbody tr {
    height: auto !important;
    min-height: 40px;
  }

  @media (max-width: 600px) {
    .modern-table >>> tbody td {
      font-size: 11px;
      padding: 5px 6px !important;
    }

    .modern-table >>> thead th {
      font-size: 10px !important;
      padding: 6px 4px !important;
      height: 36px !important;
    }

    .modern-table >>> tbody tr {
      min-height: 36px;
    }
  }

  .modern-table >>> tbody tr {
    border-bottom: 1px solid #f0f0f0;
  }

  /* Hide expand column - Vuetify automatically adds this column when show-expand is true */
  .modern-table >>> th[data-column="data-table-expand"],
  .modern-table >>> td[data-column="data-table-expand"] {
    display: none !important;
    width: 0 !important;
    padding: 0 !important;
    min-width: 0 !important;
  }

  .modern-table >>> .v-data-table__expand-icon {
    display: none !important;
  }

  /* Compact row styling */
  .modern-table >>> .v-data-table__wrapper {
    overflow-x: auto;
  }

  .modern-table >>> .v-data-table {
    border-collapse: separate;
    border-spacing: 0;
  }

  .table-row {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .table-row:hover {
    background: linear-gradient(90deg, rgba(63, 81, 181, 0.05) 0%, rgba(63, 81, 181, 0.02) 100%) !important;
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .search-field >>> .v-input__control {
    border-radius: 8px;
  }

  .v-chip {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .v-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Ensure actions are perfectly centered under the Actions header */
  .modern-table >>> th[data-column="actions"],
  .modern-table >>> td[data-column="actions"] {
    text-align: center !important;
  }

  .actions-cell {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .action-btn:hover {
    transform: scale(1.15) rotate(5deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .delete-dialog {
    border-radius: 12px;
    overflow: hidden;
  }

  .delete-dialog >>> .v-card__title {
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  /* Smooth scrollbar */
  .modern-table >>> .v-data-table__wrapper {
    scrollbar-width: thin;
    scrollbar-color: #bdbdbd #f5f5f5;
  }

  .modern-table >>> .v-data-table__wrapper::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 4px;
  }

  .modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 4px;
  }

  .modern-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
    background: #9e9e9e;
  }

  /* Avatar animations */
  .v-avatar {
    transition: all 0.3s ease;
  }

  .table-row:hover .v-avatar {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(63, 81, 181, 0.3);
  }

  /* Expanded row styling */
  .expanded-row {
    background-color: #fafafa !important;
    border-top: 2px solid #e0e0e0;
  }

  .companies-display-modern {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 20px;
  }

  .companies-count-badge {
    display: flex;
    align-items: center;
    background-color: rgba(63, 81, 181, 0.1);
    border: 1px solid rgba(63, 81, 181, 0.3);
    border-radius: 10px;
    padding: 1px 6px;
    min-width: 28px;
    justify-content: center;
  }

  .companies-count-number {
    font-size: 11px;
    font-weight: 600;
    color: #3f51b5;
    line-height: 1;
  }

  .companies-names {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 2px;
    flex: 1;
  }

  .company-name-modern {
    font-size: 12px;
    font-weight: 500;
    color: #424242;
    line-height: 1.4;
  }

  .company-separator-modern {
    margin-right: 4px;
    color: #bdbdbd;
  }

  .company-more-modern {
    font-size: 11px;
    font-weight: 600;
    color: #3f51b5;
    margin-left: 2px;
  }

  .company-more-modern.clickable {
    cursor: pointer;
    text-decoration: underline;
    transition: all 0.2s ease;
  }

  .company-more-modern.clickable:hover {
    color: #303f9f;
    text-decoration: none;
  }

  .expanded-companies-content {
    padding: 12px 16px;
  }

  .expanded-companies-header {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
  }

  .expanded-companies-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  /* Password cell styling */
  .password-cell {
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;
    padding: 2px 6px;
    margin: -2px -6px;
    border-radius: 4px;
  }

  .password-cell:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }

  .password-masked {
    color: #9e9e9e;
    font-family: monospace;
    letter-spacing: 2px;
  }

  .password-visible {
    color: #424242;
    font-family: monospace;
  }

  .password-toggle-icon {
    opacity: 0.6;
    transition: opacity 0.2s ease;
  }

  .password-cell:hover .password-toggle-icon {
    opacity: 1;
  }

  /* Provider badge styling */
  .provider-badge {
    display: inline-block;
    padding: 1px 6px;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    border-radius: 3px;
    border: 1px solid;
    transition: all 0.2s ease;
    line-height: 1.4;
    margin-left: -8px;
    align-self: flex-start;
  }

  .compact-id {
    padding: 2px 0;
    align-items: flex-start;
  }

  .provider-badge:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 600px) {
    .provider-badge {
      font-size: 9px;
      padding: 2px 6px;
    }

    .expanded-row {
      font-size: 12px;
    }

    .expanded-row .border-top {
      border-top: 1px solid #e0e0e0 !important;
    }
  }
</style>
